'use client';
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    id: 'faq-001',
    q: 'Do I need design skills to use DESIGNHUB AI?',
    a: 'No. DESIGNHUB AI is built for people with no design experience. You describe what you need in plain language, and our multi-agent AI system creates professional results. You can make simple edits or let the AI handle everything.',
  },
  {
    id: 'faq-002',
    q: 'What is an AI credit and how many do I need?',
    a: 'An AI credit is consumed each time you generate content. Image generation costs 3–5 credits, video generation costs 15–40 credits depending on length, and text generation costs 1 credit. The credit cost is shown before you confirm any generation.',
  },
  {
    id: 'faq-003',
    q: 'Can I use my designs commercially?',
    a: 'Yes, on Business, Pro, and Agency plans. Personal plan designs are for personal use only. All exported assets on paid commercial plans include full commercial rights.',
  },
  {
    id: 'faq-004',
    q: 'Can the AI remember my brand for future projects?',
    a: 'Yes. Your brand kit — including logo, colors, fonts, contact info, products, and previous campaigns — is saved to your profile with your consent. Every future design automatically uses your approved brand identity.',
  },
  {
    id: 'faq-005',
    q: 'What languages does DESIGNHUB AI support?',
    a: 'Norwegian, English, Turkish, French, German, Spanish, Italian, Dutch, Arabic, Swedish, Danish, and additional languages. Our Localization Agent adapts not just text but also cultural references, date formats, and currency.',
  },
  {
    id: 'faq-006',
    q: 'Can I publish directly to Instagram and TikTok?',
    a: 'Direct publishing requires official platform API approval. We support scheduled uploads and draft publishing where approved. You can always download the correctly formatted file for manual upload.',
  },
  {
    id: 'faq-007',
    q: 'Is my business data and brand information secure?',
    a: 'Yes. All data is stored with Row Level Security. Your private assets are never used to train shared AI models without explicit consent. You can export or delete your data at any time. We are designed with privacy in mind.',
  },
  {
    id: 'faq-008',
    q: 'Can I create restaurant menus with QR codes?',
    a: 'Yes. Restaurant and food business users get a dedicated QR Menu Studio. You can create digital menus, QR ordering links, QR Wi-Fi cards, Google Review QR codes, and table-number QR codes — all with custom colors, logos, and frame text.',
  },
];

export default function FAQSection() {
  const [open, setOpen] = useState<string | null>('faq-001');

  return (
    <section className="py-14 px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-300 text-base sm:text-lg">
            Everything you need to know before you start creating.
          </p>
        </div>

        <div className="space-y-2.5">
          {faqs?.map((faq) => (
            <div
              key={faq?.id}
              className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                open === faq?.id ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'
              }`}
            >
              <button
                onClick={() => setOpen(open === faq?.id ? null : faq?.id)}
                className="w-full flex items-center justify-between p-4 sm:p-5 text-left"
                aria-expanded={open === faq?.id}
              >
                <span className="font-semibold text-foreground text-sm pr-4">{faq?.q}</span>
                <ChevronDown
                  size={18}
                  className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${
                    open === faq?.id ? 'rotate-180 text-primary' : ''
                  }`}
                />
              </button>
              {open === faq?.id && (
                <div className="px-4 sm:px-5 pb-4 sm:pb-5 animate-fade-in">
                  <p className="text-sm text-slate-300 leading-relaxed">{faq?.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}